import { cva, type VariantProps } from 'class-variance-authority'
import { cloneElement, forwardRef, isValidElement, type ForwardRefExoticComponent, type ReactElement } from 'react'
import invariant from 'tiny-invariant'

import { cn } from '@/legacy-src/lib/utils'

import type { IconProps } from '@hedepy/icons/Icon'

import { Button, type ButtonProps, type ButtonWithRefProps } from './Button'
import { ButtonSize } from './Button.constants'

const variants = cva('flex items-center justify-center', {
  variants: {
    size: {
      [`${ButtonSize.BIG}`]: 'h-[2.75rem] w-[2.75rem] p-0',
      [`${ButtonSize.MEDIUM}`]: 'h-[3.375rem] w-[3.375rem] p-0',
      [`${ButtonSize.SMALL}`]: 'h-[2.75rem] w-[2.75rem] p-0',
    },
  },
})

interface ButtonIconVariantProps extends VariantProps<typeof variants> {}

export type OmitKeys = 'prefixIcon' | 'suffixIcon' | 'size' | 'children'

export type ExtendButton<T extends object> = OmitKeys extends keyof T
  ? DistributiveExtend<
      DistributiveOmit<T, OmitKeys>,
      {
        size: NonNullable<ButtonIconVariantProps['size']>
        children: ReactElement<IconProps>
      }
    >
  : never

export type ButtonIconProps = ExtendButton<ButtonProps>

export type ButtonIconWithRefProps = ExtendButton<ButtonWithRefProps>

export const ButtonIcon = forwardRef<HTMLButtonElement, ButtonIconProps>(({ children, ...props }, ref) => {
  invariant(isValidElement(children), 'ButtonIcon requires a single child of type Icon')

  return (
    <Button {...props} className={cn(variants({ size: props.size, className: props.className }))} ref={ref}>
      {cloneElement(children, { size: props.size === 'small' ? 16 : 24 })}
    </Button>
  )
}) as ForwardRefExoticComponent<ButtonIconWithRefProps>

ButtonIcon.displayName = 'ButtonIcon'
